/**
 * Settings — налаштування застосунку.
 *
 * Головне, чого тут немає: нічого не йде на сервер. Усе, включно зі списком
 * Gemini API-ключів, лежить у localStorage цього браузера. Ключі потрапляють у
 * тіло запиту до /api/generate і далі в ротацію на Worker.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Check,
  Copy,
  Download,
  Eye,
  EyeOff,
  KeyRound,
  Palette,
  RotateCcw,
  Settings2,
  Sliders,
  Trash2,
  TriangleAlert,
  Upload,
  Volume2,
  Zap,
} from 'lucide-react'

import { Field, Alert, Segmented, Badge } from './ui.jsx'
import {
  getSettings,
  getSettingsWriteError,
  setSettings,
  resetSettings,
  maskApiKey,
  exportSettings,
  importSettings,
  keysToText,
  normalizeKeys,
} from '../lib/settings.js'
import { TTS_ENGINES, isEngineSupported, normalizeTtsEngine } from '../lib/speech.js'
import { ukrainianVoices, onVoicesChanged } from '../lib/browserTts.js'
import { ALGORITHMS } from '../lib/scheduler.js'

const GEMINI_MODELS = [
  { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', hint: 'найновіший, найкраща якість' },
  { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', hint: 'стабільна попередня версія' },
  { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite', hint: 'дешевше, швидше' },
  { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro Preview', hint: 'повільно, найкраще міркує' },
]

export default function Settings({ onClose, onKeysChanged, onChanged }) {
  const [settings, setLocal] = useState(() => getSettings())
  const [voices, setVoices] = useState([])
  const [keysText, setKeysText] = useState(() => keysToText(settings.geminiKeys))
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(null)
  const [saveState, setSaveState] = useState({ status: 'idle' })
  const [importText, setImportText] = useState('')
  const [category, setCategory] = useState('ai')
  const [numberDrafts, setNumberDrafts] = useState({})
  const saveTimer = useRef(null)
  const statusTimer = useRef(null)
  const pendingPatch = useRef({})

  // Таймери автозбереження чистимо при закритті налаштувань
  useEffect(() => {
    const unsub = onVoicesChanged(() => setVoices(ukrainianVoices()))
    setVoices(ukrainianVoices())
    return unsub
  }, [])

  useEffect(
    () => () => {
      clearTimeout(saveTimer.current)
      clearTimeout(statusTimer.current)
    },
    [],
  )

  const showSaved = useCallback(() => {
    clearTimeout(statusTimer.current)
    setSaveState({ status: 'saved' })
    statusTimer.current = setTimeout(() => setSaveState({ status: 'idle' }), 2500)
  }, [])

  const draftKeys = useMemo(() => normalizeKeys(keysText), [keysText])
  const selectedEngine = normalizeTtsEngine(settings.ttsEngine)
  const engineSupported = isEngineSupported(selectedEngine)

  const persistNow = useCallback(
    (patch = {}) => {
      if (!patch || Object.keys(patch).length === 0) return
      setSaveState({ status: 'saving' })
      try {
        const next = setSettings(patch)
        if (getSettingsWriteError()) throw getSettingsWriteError()
        setLocal(next)
        onChanged?.(next)
        if ('geminiKeys' in patch) onKeysChanged?.(next.geminiKeys)
        showSaved()
      } catch {
        setSaveState({ status: 'error' })
      }
    },
    [onChanged, onKeysChanged, showSaved],
  )

  const clearPending = useCallback(() => {
    clearTimeout(saveTimer.current)
    pendingPatch.current = {}
  }, [])

  const persistDebounced = useCallback(
    (patch = {}) => {
      pendingPatch.current = { ...pendingPatch.current, ...patch }
      clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => {
        const pending = pendingPatch.current
        pendingPatch.current = {}
        persistNow(pending)
      }, 700)
    },
    [persistNow],
  )

  const flushPending = useCallback(() => {
    const pending = pendingPatch.current
    clearPending()
    persistNow(pending)
  }, [clearPending, persistNow])

  const updateAndSave = useCallback((patch) => persistNow(patch), [persistNow])

  const handleKeysChange = (value) => {
    setKeysText(value)
    persistDebounced({ geminiKeys: normalizeKeys(value) })
  }

  const removeKey = (key) => {
    const nextText = keysToText(draftKeys.filter((x) => x !== key))
    setKeysText(nextText)
    clearPending()
    persistNow({ geminiKeys: normalizeKeys(nextText) })
  }

  const numberValue = (key, fallback) => numberDrafts[key] ?? settings[key] ?? fallback

  const handleNumberChange = (key, raw) => {
    setNumberDrafts((prev) => ({ ...prev, [key]: raw }))
  }

  const commitNumber = (key, raw, { min, max, fallback }) => {
    const parsed = Number(raw)
    const value = Math.min(max, Math.max(min, Number.isFinite(parsed) ? parsed : fallback))
    setNumberDrafts((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    persistNow({ [key]: value })
  }

  const doReset = () => {
    setSaveState({ status: 'saving' })
    try {
      const next = resetSettings()
      if (getSettingsWriteError()) throw getSettingsWriteError()
      setLocal(next)
      setKeysText(keysToText(next.geminiKeys))
      setNumberDrafts({})
      onChanged?.(next)
      onKeysChanged?.(next.geminiKeys)
      showSaved()
    } catch {
      setSaveState({ status: 'error' })
    }
  }

  const doImport = (text) => {
    setSaveState({ status: 'saving' })
    try {
      JSON.parse(text)
      const next = importSettings(text)
      if (getSettingsWriteError()) throw getSettingsWriteError()
      setLocal(next)
      setKeysText(keysToText(next.geminiKeys))
      setNumberDrafts({})
      setImportText('')
      onChanged?.(next)
      onKeysChanged?.(next.geminiKeys)
      showSaved()
    } catch {
      setSaveState({ status: 'error' })
    }
  }

  return (
    <div className="settings-shell pb-8">
      <aside className="settings-nav">
        <p className="section-kicker">Налаштування</p>
        {[['ai', 'Генерація'], ['learning', 'Повторення'], ['speech', 'Озвучка'], ['interface', 'Інтерфейс'], ['data', 'Дані']].map(([id, label]) => <button key={id} type="button" onClick={() => setCategory(id)} className={category === id ? 'is-active' : ''}>{label}</button>)}
      </aside>
      <div className="settings-content">
      {/* ═══════════════ Gemini API-ключі ═══════════════ */}
      <section className={`settings-section ${category !== 'ai' ? 'hidden' : ''}`}>
        <header className="mb-4 flex items-start gap-3">
          <div className="rounded-xl bg-brand-100 p-2.5 text-brand-700 dark:bg-brand-950/60 dark:text-brand-300">
            <KeyRound className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold">Gemini API-ключі</h2>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              Worker бере навмання один із ключів і при помилці <b>429</b> автоматично переходить на
              наступний. Ще більше ключів — більше паралельних запитів без витрачання квоти на одному.
            </p>
          </div>
          {draftKeys.length > 0 && (
            <Badge className="shrink-0 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200">
              {draftKeys.length} ключ(ів)
            </Badge>
          )}
        </header>

        <Field
          label="Список ключів"
          hint="Один ключ на рядок або через кому. Отримати: console → Google AI Studio → Get API key."
        >
          <div className="relative">
              <textarea
                className={`input min-h-28 resize-y font-mono text-[13px] ${revealed ? '' : 'blur-[3px] select-none'}`}
                value={keysText}
                onChange={(e) => handleKeysChange(e.target.value)}
                onBlur={flushPending}
                placeholder={'AIzaSy...\nAIzaSy...\nAIzaSy...'}
              spellCheck={false}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              className="btn-ghost absolute top-2 right-2 !px-2 !py-1"
              title={revealed ? 'Приховати' : 'Показати'}
            >
              {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>

        {draftKeys.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {draftKeys.map((k, i) => (
              <div
                key={k}
                className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-1.5 text-xs dark:bg-slate-950/60"
              >
                <span className="w-4 shrink-0 text-center font-bold text-slate-400">{i + 1}</span>
                <code className="flex-1 truncate font-mono text-slate-600 dark:text-slate-300">
                  {maskApiKey(k)}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(k)
                    setCopied(k)
                    setTimeout(() => setCopied((c) => (c === k ? null : c)), 1500)
                  }}
                  className={`btn-ghost !px-1.5 !py-0.5 ${copied === k ? 'text-emerald-600 dark:text-emerald-400' : ''}`}
                  title="Скопіювати повний ключ у буфер"
                >
                  {copied === k ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={() => removeKey(k)}
                  className="btn-ghost !px-1.5 !py-0.5 text-rose-600 dark:text-rose-400"
                  title="Видалити ключ"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <Field label="Модель">
          <select
            className="input"
            value={settings.geminiModel}
            onChange={(e) => updateAndSave({ geminiModel: e.target.value })}
          >
            {GEMINI_MODELS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label} — {m.hint}
              </option>
            ))}
          </select>
        </Field>

        <Alert tone="info" className="mt-4">
          Ключі зберігаються тільки в <b>localStorage</b> цього браузера. Вони не потрапляють у D1, не
          пишуться в логи Worker і не надсилаються нікуди, крім <code>/api/generate</code>. Не
          зберігайте ключі на спільному комп’ютері.
        </Alert>
      </section>

      {/* ═══════════════ Алгоритм повторення ═══════════════ */}
      <section className={`settings-section ${category !== 'learning' ? 'hidden' : ''}`}>
        <header className="mb-4 flex items-start gap-3">
          <div className="rounded-xl bg-emerald-100 p-2.5 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
            <Zap className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-bold">Інтервальне повторення</h2>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              Кнопки «Сложно / Нормально / Идеально» перераховують інтервал за обраним алгоритмом.
            </p>
          </div>
        </header>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Алгоритм">
            <Segmented
              value={settings.algorithm}
              onChange={(v) => updateAndSave({ algorithm: v })}
              options={Object.entries(ALGORITHMS).map(([value, a]) => ({
                value,
                label: a.label,
                icon: a.default ? <Check className="h-3.5 w-3.5" /> : null,
              }))}
            />
          </Field>

          <Field
            label={`Бажана ймовірність пригадування — ${Math.round(settings.desiredRetention * 100)}%`}
            hint="Нижче = картки повертаються частіше, але ви їх не забудете. 90% — золота середина."
          >
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={70}
                max={97}
                value={Math.round(settings.desiredRetention * 100)}
                onChange={(e) => updateAndSave({ desiredRetention: Number(e.target.value) / 100 })}
                className="flex-1 accent-brand-600"
              />
              <span className="w-11 shrink-0 text-right text-sm font-semibold tabular-nums">
                {Math.round(settings.desiredRetention * 100)}%
              </span>
            </div>
          </Field>

          <Field label="Нових карток на день">
            <input
              type="number"
              min={0}
              max={200}
              className="input tabular-nums"
              value={numberValue('newPerDay', 0)}
              onChange={(e) => handleNumberChange('newPerDay', e.target.value)}
              onBlur={(e) => commitNumber('newPerDay', e.target.value, { min: 0, max: 200, fallback: 0 })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur()
              }}
            />
          </Field>

          <Field label="Максимум повторень за день">
            <input
              type="number"
              min={10}
              max={1000}
              className="input tabular-nums"
              value={numberValue('maxReviewsPerDay', 200)}
              onChange={(e) => handleNumberChange('maxReviewsPerDay', e.target.value)}
              onBlur={(e) => commitNumber('maxReviewsPerDay', e.target.value, { min: 10, max: 1000, fallback: 200 })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur()
              }}
            />
          </Field>
        </div>

        {settings.algorithm === 'fsrs' && (
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            FSRS-4.5 зменшує інтервал для «важких» карток і збільшує для «легких», враховуючи
            складність кожної окремо. SM-2 — класика, простіше й передбачуваніше.
          </p>
        )}
      </section>

      {/* ═══════════════ Озвучка й наголоси ═══════════════ */}
      <section className={`settings-section ${category !== 'speech' ? 'hidden' : ''}`}>
        <header className="mb-4 flex items-start gap-3">
          <div className="rounded-xl bg-rose-100 p-2.5 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">
            <Volume2 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-bold">Озвучка та наголоси</h2>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              Для карток із тегом <code className="chip">#наголоси</code> ударна буква підсвічується
              червоним. Виберіть основний рушій озвучки нижче.
            </p>
          </div>
        </header>

        <Field
          label="Рушій озвучки"
          hint="Google — основний український голос. Системний голос — запасний рушій, якщо Google недоступний."
        >
          <Segmented
            value={selectedEngine}
            onChange={(v) => updateAndSave({ ttsEngine: v })}
            options={TTS_ENGINES.map((engine) => ({ value: engine.id, label: engine.label }))}
          />
        </Field>

        {!engineSupported && (
          <Alert tone="warn" className="mb-4">
            Ваш браузер не підтримує відтворення аудіо — озвучка буде недоступна. Підсвітка наголосів
            працює незалежно.
          </Alert>
        )}

        <div className="space-y-3">
          <Toggle
            label="Підсвічувати ударну букву"
            hint="Червона й підкреслена буква там, де стоїть U+0301."
            checked={settings.highlightStress}
            onChange={(v) => updateAndSave({ highlightStress: v })}
          />
          <Toggle
            label="Кнопка озвучки на картці"
            hint="Використовує український голос Google; знаки наголосу готуються перед озвучкою."
            checked={settings.speechEnabled}
            onChange={(v) => updateAndSave({ speechEnabled: v })}
            disabled={!engineSupported}
          />
          <Toggle
            label="Озвучувати автоматично після перевертання"
            hint="Зручно для мови, але дратує на математиці — краще вимкнути."
            checked={settings.autoSpeak}
            onChange={(v) => updateAndSave({ autoSpeak: v })}
            disabled={!engineSupported || !settings.speechEnabled}
          />
          <Toggle
            label="Показувати четверту кнопку «Забув»"
            hint="Додає оцінку again: картка повернеться вже сьогодні."
            checked={settings.showAgainButton}
            onChange={(v) => updateAndSave({ showAgainButton: v })}
          />
        </div>

        {selectedEngine === 'browser' && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label={`Швидкість мовлення — ${settings.speechRate.toFixed(2)}×`}>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={50}
                  max={150}
                  value={Math.round(settings.speechRate * 100)}
                  onChange={(e) => updateAndSave({ speechRate: Number(e.target.value) / 100 })}
                  className="flex-1 accent-brand-600"
                />
                <span className="w-11 shrink-0 text-right text-sm font-semibold tabular-nums">
                  {settings.speechRate.toFixed(2)}×
                </span>
              </div>
            </Field>

            <Field
              label="Системний голос"
              hint={
                voices.length
                  ? `Знайдено українських голосів: ${voices.length}`
                  : 'Українського голосу в системі не знайдено — буде стандартний.'
              }
            >
              <select
                className="input"
                value={settings.speechVoice}
                onChange={(e) => updateAndSave({ speechVoice: e.target.value })}
                disabled={!voices.length}
              >
                <option value="">Автоматично</option>
                {voices.map((v) => (
                  <option key={v.name} value={v.name}>
                    {v.name} ({v.lang}){v.localService ? ' — локальний' : ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}
      </section>

      {/* ═══════════════ Інтерфейс ═══════════════ */}
      <section className={`settings-section ${category !== 'interface' ? 'hidden' : ''}`}>
        <header className="mb-4 flex items-start gap-3">
          <div className="rounded-xl bg-slate-200 p-2.5 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
            <Sliders className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-bold">Інтерфейс і сесії</h2>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              Розмір сесії, тема та клавіатурні підказки.
            </p>
          </div>
        </header>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Карток у сесії">
            <input
              type="number"
              min={5}
              max={200}
              className="input tabular-nums"
              value={numberValue('sessionSize', 20)}
              onChange={(e) => handleNumberChange('sessionSize', e.target.value)}
              onBlur={(e) => commitNumber('sessionSize', e.target.value, { min: 5, max: 200, fallback: 20 })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur()
              }}
            />
          </Field>
          <Field label="Тема">
            <Segmented
              value={settings.theme}
              onChange={(v) => updateAndSave({ theme: v })}
              options={[
                { value: 'light', label: 'Світла', icon: <Palette className="h-4 w-4" /> },
                { value: 'dark', label: 'Темна', icon: <Palette className="h-4 w-4" /> },
              ]}
            />
          </Field>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/50">
          <p className="label">Клавіатура</p>
          <ul className="grid gap-1.5 text-sm sm:grid-cols-2">
            {[
              ['Space / Enter', 'показати відповідь / далі'],
              ['H', 'підказка'],
              ['S', 'озвучити'],
              ['1 / 2 / 3', 'Сложно / Нормально / Идеально'],
              ['4', 'Забув (якщо ввімкнено)'],
              ['F', 'перевернути картку у списку'],
            ].map(([k, v]) => (
              <li key={k} className="flex items-center gap-2">
                <span className="kbd w-24 shrink-0 text-center">{k}</span>
                <span className="text-slate-500 dark:text-slate-400">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═══════════════ Дані ═══════════════ */}
      <section className={`settings-section ${category !== 'data' ? 'hidden' : ''}`}>
        <header className="mb-4 flex items-start gap-3">
          <div className="rounded-xl bg-violet-100 p-2.5 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300">
            <Settings2 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-bold">Дані</h2>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              Налаштування можна перенести на інший комп’ютер. Картки й конспекти живуть у D1 і
              експортуються звідти.
            </p>
          </div>
        </header>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              const blob = new Blob([exportSettings()], { type: 'application/json' })
              const a = document.createElement('a')
              a.href = URL.createObjectURL(blob)
              a.download = 'nmt-settings.json'
              a.click()
              URL.revokeObjectURL(a.href)
            }}
            className="btn-outline"
          >
            <Download className="h-4 w-4" /> Експорт
          </button>

          <label className="btn-outline cursor-pointer">
            <Upload className="h-4 w-4" /> Імпорт
            <input
              type="file"
              accept="application/json"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (file) doImport(await file.text())
                e.target.value = ''
              }}
            />
          </label>

          <button type="button" onClick={doReset} className="btn-ghost text-rose-600 dark:text-rose-400">
            <RotateCcw className="h-4 w-4" /> Скинути все
          </button>
        </div>

        <details className="mt-3">
          <summary className="cursor-pointer text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
            Вставити налаштування вручну (JSON)
          </summary>
          <textarea
            className="input mt-2 min-h-24 font-mono text-xs"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder='{"algorithm": "fsrs", ...}'
          />
          <button
            type="button"
            onClick={() => doImport(importText)}
            className="btn-outline mt-2"
            disabled={!importText.trim()}
          >
            Застосувати
          </button>
        </details>
      </section>

      {/* Автозбереження: окремої кнопки «Зберегти» більше немає */}
      </div>      <div className="safe-bottom settings-savebar sticky bottom-0 -mx-4 flex items-center gap-3 px-4 py-3 sm:-mx-5 sm:px-5">
        <span
          className={`save-status ${saveState.status === 'error' ? 'is-error' : ''}`}
          role="status"
          aria-live="polite"
        >
          {saveState.status === 'saved' && <Check className="h-3.5 w-3.5" />}
          {saveState.status === 'error' && <TriangleAlert className="h-3.5 w-3.5" />}
          {saveState.status === 'saving'
            ? 'Збереження…'
            : saveState.status === 'saved'
              ? 'Збережено'
              : saveState.status === 'error'
                ? 'Не вдалося зберегти налаштування'
                : 'Зміни зберігаються автоматично'}
        </span>
        {onClose && (
          <button type="button" onClick={onClose} className="btn-ghost">
            Закрити
          </button>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function Toggle({ label, hint, checked, onChange, disabled = false }) {
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border border-slate-200 px-3.5 py-3 transition
                  dark:border-slate-800 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-950/50'}`}
      onClick={() => !disabled && onChange(!checked)}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition ${
          checked ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-700'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
            checked ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{hint}</span>}
      </span>
    </div>
  )
}
